import { arrayOf, bool, string, shape } from 'prop-types';
import React from 'react';
import classnames from 'classnames';
import AmpImg from 'components/AmpImg';
import Figure from 'components/Figure';
import SaveToWishListCSR from 'components/SaveToWishList/SaveToWishListCSR';
import Permutive from 'components/Permutive';
import withCompositeProduct from 'lib/withCompositeProduct';
import { getAffiliateLinkProps } from 'util/affiliates/amazon';
import { MQ_MIDLARGE, MQ_LARGE, MQ_MEDIUM } from 'constants/mq';
import { HAS_WISH_LISTS } from 'constants/site';
import styles from './styles';

const SPONSORED = 'sponsored';

/**
 * This component is used for rendering product information for 'productEmbed'
 * embeds, as well as the deprecated 'product' and 'productTeaser' embeds.
 * The differences in the data are normalized by the withCompositeProduct adapter.
 */
export function Product(props) {
  const {
    description,
    humanReadableId,
    imageUrl,
    isAffiliate,
    isAmp,
    name,
    price,
    retailerName,
    salePrice,
    tags,
    url: originalUrl,
    variant
  } = props;

  const wrapperClasses = classnames('Product', {
    'Product--button': !(
      price ||
      description ||
      imageUrl ||
      name ||
      retailerName
    ),
    [`Product--${variant}`]: variant
  });

  // handle tagging affiliate links
  const linkProps = isAffiliate
    ? getAffiliateLinkProps(originalUrl)
    : {
        href: originalUrl
      };

  const tagSlugs = (tags || []).map(tag => tag.slug);
  const isSponsored = tags && tags.map(t => t.slug).includes(SPONSORED);

  const baseLinkProps = {
    rel: 'nofollow',
    target: '_blank',
    onClick: () => {
      Permutive.track('AffiliateLinkClick', {
        product: {
          name,
          price: price ? parseFloat(price.replace('$', '')) : 0,
          tags: tagSlugs,
          retailer: retailerName
        },
        href: linkProps.href
      });
    },
    'data-gtm-trigger': 'product-buy-link-click',
    'data-gtm-product-block': 'single-product',
    'data-gtm-product-id': humanReadableId,
    'data-gtm-product-retailers': retailerName,
    'data-gtm-product-tags': tagSlugs.join(','),
    ...linkProps
  };

  return (
    // NOTE, typically noopener and noreferrer should be used when opening
    // links in a new tab. However it is unclear if this breaks certain affiliate
    // services, or violates Amazon's terms of use.
    <div className={wrapperClasses}>
      {imageUrl && (
        <a
          className="Product__image"
          data-test="image-affiliate-link"
          aria-label={name}
          {...baseLinkProps}
        >
          {isAmp ? (
            <AmpImg
              url={imageUrl}
              alt={`Product Image: ${name}`}
              originalWidth={1}
              originalHeight={1}
              maxSize={{
                width: 534
              }}
            />
          ) : (
            <Figure
              alt={`Product Image: ${name}`}
              isPinnable={false}
              maxWidth={365}
              originalHeight={365}
              originalWidth={365} // force them all to be 1:1
              placement="product"
              sourceSizes={[
                {
                  media: MQ_MIDLARGE,
                  layoutWidth: {
                    value: 365,
                    units: 'px'
                  }
                },
                {
                  media: MQ_LARGE,
                  layoutWidth: {
                    value: 305,
                    units: 'px'
                  }
                },
                {
                  media: MQ_MEDIUM,
                  layoutWidth: {
                    value: 348,
                    units: 'px'
                  }
                },
                {
                  layoutWidth: {
                    value: 100,
                    units: 'vw'
                  }
                }
              ]}
              resizeMode="scaleFit"
              url={imageUrl}
            />
          )}
        </a>
      )}
      {isSponsored && <div className="Product__cake">Partner Pick</div>}
      <div className="Product__body">
        <a
          className="Product__content-link"
          data-test="content-affiliate-link"
          {...baseLinkProps}
        >
          {name && <div className="Product__name">{name}</div>}

          {price && (
            <div className="Product__price">
              {salePrice && (
                <span
                  className="Product__original-price"
                  data-test="originalPrice"
                >
                  {price}
                </span>
              )}
              <span className="Product__current-price" data-test="currentPrice">
                {salePrice || price}
              </span>
            </div>
          )}

          {retailerName && (
            <div className="Product__retailer" data-test="retailer">
              {retailerName}
            </div>
          )}

          {description && (
            <div
              className="Product__description"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
        </a>

        <a
          className="Product__cta"
          aria-label="buy now"
          data-test="buy-now-affiliate-link"
          {...baseLinkProps}
        >
          Buy Now
        </a>

        {HAS_WISH_LISTS && humanReadableId && (
          <div className="Product__saveToWishList">
            <SaveToWishListCSR
              product={{
                id: humanReadableId,
                imageUrl,
                name,
                retailerName,
                tags,
                originalUrl
              }}
              source="single-product"
            />
          </div>
        )}
      </div>
      <style jsx>{styles}</style>
    </div>
  );
}

Product.propTypes = {
  description: string,
  humanReadableId: string,
  image: shape({
    url: string
  }),
  imageUrl: string,
  isAffiliate: bool,
  isAmp: bool,
  name: string,
  price: string,
  retailerName: string,
  salePrice: string,
  tags: arrayOf(
    shape({
      slug: string.isRequired
    })
  ),
  url: string.isRequired,
  variant: string
};

Product.defaultProps = {
  description: undefined,
  humanReadableId: undefined,
  image: undefined,
  imageUrl: undefined,
  isAffiliate: false,
  isAmp: false,
  name: undefined,
  price: undefined,
  retailerName: undefined,
  salePrice: undefined,
  tags: undefined,
  variant: 'in-post'
};

export default withCompositeProduct(Product);
